import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import {
    getMeetingByAppointment,
    startAdhocSession,
    startMeeting,
    getMeeting,
    admitParticipant,
    holdParticipant,
    endMeeting,
    addParticipant as apiAddParticipant,
    inviteParticipant,
    getInviteAvailability,
    shareContent as apiShareContent,
    unshareContent as apiUnshareContent,
    setPen as apiSetPen,
} from '@/common/api/telehealth.api';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useNotificationStore } from '@/stores/notification';

const POLL_MS = 5000;
/**
 * While a form is shared, the poll is also how the host learns the participant
 * typed something (via `sharedContent.answerSeq`), so 5s reads as laggy. OT runs
 * its telehealth poll at 2s and its host mirror is usable on it.
 */
const POLL_MS_SHARING_FORM = 2000;

/**
 * The live telehealth session for the HOST (rendering provider). Holds the single
 * Daily call object and drives the right-docked video panel (#5015). Lives at the
 * app-shell level so the call survives chart navigation — one active session.
 *
 * BNT/Fresh own the roster, admit, invite and add flows (polled from hostView);
 * Daily owns the media (tiles, mic/camera, mute/remove). A participant's video
 * appears only once they've been admitted AND have joined the Daily room. Recording is intentionally out of
 * scope here; screen share is host-only (see startScreenShare).
 */
export const useTelehealthSessionStore = defineStore('telehealthSession', () => {
    const notify = () => useNotificationStore();

    // --- Dock UI state ---
    const open = ref(false); // dock visible
    const dockState = ref('docked'); // 'docked' | 'collapsed' | 'fullscreen'
    const loading = ref(false);
    const errorMsg = ref('');

    // --- Meeting (hostView) + invites ---
    // meeting: { id, status, mode, hostLink,
    //            participants: [{ id, bntUserId, type, displayName, presence }] }
    const meeting = ref(null);
    const inviteChannels = ref({}); // { [participantId]: { sms, email } }
    const invitingId = ref(null);
    const addOpen = ref(false);
    const addStage = ref('search'); // 'search' | 'invite'
    const addedContact = ref(null);
    const addBusy = ref(false);

    // --- Sharing (#5040) ---
    const shareMenuOpen = ref(false);
    const shareBusy = ref(false);
    /** What's shared right now: { kind, ref, title, pen, sharedAt } or null. */
    const sharedContent = computed(() => meeting.value?.sharedContent ?? null);
    const isSharing = computed(() => sharedContent.value !== null);
    /** True while a participant holds the pen — drives the banner's toggle. */
    const participantCanEdit = computed(() => sharedContent.value?.pen === 'participant');
    /** Forms available to share: [{ id, taskId, title, status, ready }]. */
    const encounterForms = ref([]);
    const formsLoading = ref(false);
    /**
     * Whether the appointment has an encounter at all. Distinct from "no forms":
     * an appointment only gets an encounter when one is created for the attendee
     * (from the appointment, with a program), and until then there is nowhere
     * for a form to live — a different problem needing a different fix.
     */
    const hasEncounter = ref(false);

    // --- Daily (call object is intentionally NOT reactive) ---
    let call = null;
    const joined = ref(false);
    const localAudio = ref(true);
    const localVideo = ref(true);
    // Keyed by Daily session_id: { sessionId, userId, userName, local, audioOn, videoOn, videoTrack, audioTrack }
    const dailyParticipants = ref({});

    let pollTimer = null;
    let currentContextId = null;
    /**
     * Bumped every time a session opens or closes. Every async action captures it
     * before its first await and re-checks after: without this, a request still in
     * flight when the host hits End resolves into a closed session — at best
     * resurrecting `meeting` behind a hidden dock, at worst joining Daily with the
     * host's camera live and no UI left to stop it.
     */
    let generation = 0;
    const isCurrent = (gen) => gen === generation;
    /**
     * Which appointment this session belongs to, exposed so a surface elsewhere
     * in the app — the form filler — can tell whether the call on screen is the
     * one for the chart it is showing, rather than some other live session.
     * Named distinctly from openForAppointment's parameter, which shadows it.
     */
    const sessionContextId = ref(null);
    /**
     * What the session hangs off: 'appointment' for a scheduled visit, or 'form'
     * for one started ad-hoc from a standalone form, which has neither an
     * appointment nor an encounter.
     */
    const sessionContextType = ref(null);
    /** Seconds since the session opened, for the call timer in the form header. */
    const elapsed = ref(0);
    let elapsedTimer = null;

    function startElapsed() {
        stopElapsed();
        elapsed.value = 0;
        elapsedTimer = setInterval(() => (elapsed.value += 1), 1000);
    }
    function stopElapsed() {
        if (elapsedTimer) clearInterval(elapsedTimer);
        elapsedTimer = null;
    }

    // ------------------------------------------------------------------
    // Derived roster: merge hostView (roles/presence/admit) with Daily (media)
    // ------------------------------------------------------------------

    /**
     * The live Daily participant for a roster row, or null. Matched by the BNT ref
     * (bntUserId = Daily's runtime user_id), falling back to the display name — the
     * id we send BNT is NOT the Daily user_id, so name is the only other shared key.
     */
    const dailyFor = (rp) => Object.values(dailyParticipants.value).find((p) => !p.local && ((rp.bntUserId && p.userId === rp.bntUserId) || (rp.displayName && p.userName === rp.displayName))) || null;

    /** The roster row for a live Daily participant (best-effort: BNT ref, then name). */
    const rosterFor = (dp) => (meeting.value?.participants ?? []).find((p) => (p.bntUserId && p.bntUserId === dp.userId) || (p.displayName && p.displayName === dp.userName)) || null;

    /**
     * Video tiles are driven by who is actually in the Daily call — the host
     * (local) plus every remote participant Daily reports — NOT by the roster.
     * The roster only enriches the name/role; a remote still gets a tile even if
     * we can't line it up to a roster row. audioTrack renders into a hidden
     * <audio> (call-object mode does not auto-play remote audio).
     */
    const tiles = computed(() => {
        const out = [];
        for (const dp of Object.values(dailyParticipants.value)) {
            if (dp.local) {
                out.unshift({ key: 'local', sessionId: dp.sessionId, name: 'You', local: true, videoTrack: dp.videoTrack, videoOn: localVideo.value, audioOn: localAudio.value });
            } else {
                const rp = rosterFor(dp);
                out.push({
                    key: dp.sessionId,
                    sessionId: dp.sessionId,
                    name: rp?.displayName || dp.userName || 'Participant',
                    role: rp?.type,
                    videoTrack: dp.videoTrack,
                    audioTrack: dp.audioTrack,
                    videoOn: dp.videoOn,
                    audioOn: dp.audioOn,
                });
            }
        }
        return out;
    });

    /** Roster rows for the panel: every participant with presence + whether they're live in the call. */
    const roster = computed(() =>
        (meeting.value?.participants ?? []).map((p) => {
            const dp = dailyFor(p);
            return {
                ...p,
                inSession: !!dp && !dp.local,
                sessionId: dp?.sessionId ?? null,
                audioOn: dp?.audioOn ?? null,
                channels: inviteChannels.value[p.id] ?? null,
            };
        }),
    );

    // ------------------------------------------------------------------
    // Lifecycle
    // ------------------------------------------------------------------

    /**
     * Bring a session up, whatever it hangs off.
     *
     * A scheduled visit is keyed on its Appointment; a session started from a
     * standalone form has no appointment and is keyed on that form's Encounter.
     * Everything after resolving the meeting — the generation guard, the timer,
     * joining Daily, polling — is identical, so it lives here once.
     *
     * @param {object}   options
     * @param {string}   options.contextId      the Appointment or Encounter id
     * @param {string}   options.contextType    'appointment' | 'encounter'
     * @param {Function} options.resolveMeeting () => the meeting to start
     * @param {string}   options.notFound       message when there is no meeting
     * @param {?Array}   options.seedForms      forms to show without looking any
     *   up, for a context that has no encounter to search
     */
    async function openSession({ contextId, contextType, resolveMeeting, notFound, seedForms = null }) {
        // Re-opening the same LIVE session just reveals the dock. A previous
        // attempt that failed is not live: it left `open` true with no meeting,
        // and short-circuiting there would make the failure unretryable.
        if (open.value && currentContextId === contextId && meeting.value) {
            if (dockState.value === 'collapsed') dockState.value = 'docked';
            return;
        }
        if (open.value) await close(); // one active session

        const gen = ++generation;
        currentContextId = contextId;
        sessionContextId.value = contextId;
        sessionContextType.value = contextType;
        startElapsed();
        open.value = true;
        dockState.value = 'docked';
        loading.value = true;
        errorMsg.value = '';
        meeting.value = null;
        try {
            const resolved = await resolveMeeting();
            if (!isCurrent(gen)) return;
            if (!resolved?.success) {
                errorMsg.value = resolved?.error || notFound;
                return;
            }
            const started = await startMeeting(resolved.data.id);
            if (!isCurrent(gen)) return;
            if (!started?.success) {
                errorMsg.value = started?.error || 'Could not start the session.';
                return;
            }
            meeting.value = started.data;
            if (seedForms) {
                encounterForms.value = seedForms;
                hasEncounter.value = true;
            }
            await joinDaily(started.data.hostLink, gen);
            if (!isCurrent(gen)) return;
            loadInviteAvailability();
            startPolling();
        } catch (e) {
            if (isCurrent(gen)) {
                errorMsg.value = e?.response?.data?.error || 'Could not start the session.';
                await teardownDaily();
                meeting.value = null;
            }
        } finally {
            if (isCurrent(gen)) loading.value = false;
        }
    }

    /** A scheduled telehealth appointment. */
    const openForAppointment = (appointmentId) =>
        openSession({
            contextId: appointmentId,
            contextType: 'appointment',
            resolveMeeting: () => getMeetingByAppointment(appointmentId),
            notFound: 'No telehealth meeting found for this appointment.',
        });

    /**
     * A session started from a standalone form, which has no appointment.
     *
     * The patient is put in the waiting room and told nothing — the clinician
     * invites them from the dock when they are ready.
     */
    const openAdhocForForm = (form, patientId) =>
        openSession({
            contextId: form.id,
            contextType: 'form',
            resolveMeeting: () => startAdhocSession(form.id, patientId),
            notFound: 'Could not start a session for this form.',
            // There is no encounter to search, and the answer is already known:
            // the form the clinician started from is the only one in play. Seed
            // it so the dock's Share a form opens it rather than finding nothing.
            seedForms: [{ id: form.id, taskId: form.taskId, title: form.title, ownerId: null, status: null, ready: true }],
        });

    async function joinDaily(hostLink, gen) {
        if (!hostLink) {
            errorMsg.value = 'The video room is not ready yet.';
            return;
        }
        // Split "https://<domain>.daily.co/<room>?t=<token>" into url + token.
        let roomUrl = hostLink;
        let token = null;
        try {
            const u = new URL(hostLink);
            token = u.searchParams.get('t');
            roomUrl = `${u.origin}${u.pathname}`;
        } catch {
            // keep hostLink as-is; Daily will parse a token in the query string
        }

        // Lazy-load the SDK so it isn't pulled into the main bundle (or tests) until
        // a session actually starts.
        const Daily = (await import('@daily-co/daily-js')).default;
        // The session may have been ended while the SDK chunk loaded. Creating a
        // call object now would join the room with the host's camera on and no
        // dock to turn it off — and would then block the next session, since
        // Daily refuses a second instance.
        if (!isCurrent(gen)) return;

        const pending = Daily.createCallObject({ subscribeToTracksAutomatically: true });
        call = pending;
        call.on('joined-meeting', onDailyChange)
            .on('participant-joined', onDailyChange)
            .on('participant-updated', onDailyChange)
            .on('participant-left', onDailyChange)
            .on('track-started', onDailyChange)
            .on('track-stopped', onDailyChange)
            .on('left-meeting', onDailyLeft)
            .on('app-message', onDailyAppMessage)
            // Local share state comes from Daily, not from our own call: the
            // browser's own "Stop sharing" bar ends it without touching our UI.
            // Note these names — there is no `started-screenshare` in this SDK.
            .on('local-screen-share-started', onScreenShareStarted)
            .on('local-screen-share-stopped', onScreenShareStopped)
            .on('local-screen-share-canceled', onScreenShareStopped)
            .on('nonfatal-error', onDailyNonFatal)
            .on('error', onDailyError);

        await pending.join(token ? { url: roomUrl, token } : { url: roomUrl });
        // Ended mid-join: leave the room we just entered rather than sitting in
        // it invisibly.
        if (!isCurrent(gen) || call !== pending) {
            try {
                await pending.leave();
            } catch {
                // ignore — already going away
            }
            try {
                pending.destroy();
            } catch {
                // ignore
            }
            if (call === pending) call = null;
            return;
        }
        joined.value = true;
        call.setLocalAudio(localAudio.value);
        call.setLocalVideo(localVideo.value);
        syncDailyParticipants();
    }

    function onDailyChange() {
        syncDailyParticipants();
    }
    function onDailyLeft() {
        joined.value = false;
        dailyParticipants.value = {};
        // Release the instance too. Daily allows only one at a time, so holding a
        // dead handle here makes the NEXT session fail to start.
        if (call) {
            try {
                call.destroy();
            } catch {
                // ignore
            }
            call = null;
        }
    }
    function onScreenShareStarted() {
        screenSharing.value = true;
        screenShareError.value = '';
        syncDailyParticipants();
    }
    function onScreenShareStopped() {
        screenSharing.value = false;
        syncDailyParticipants();
    }
    /** Screen-share failures arrive here rather than on the error event. */
    function onDailyNonFatal(e) {
        if (e?.type !== 'screen-share-error') return;
        screenSharing.value = false;
        screenShareError.value = e?.errorMsg || 'Screen sharing stopped unexpectedly.';
    }

    function onDailyError(e) {
        errorMsg.value = e?.errorMsg || 'The video connection had a problem.';
    }

    /**
     * Daily's peer data channel, used only by the ephemeral whiteboard (#5040).
     * Surfaced as the last-received payload rather than an event the store
     * dispatches, so the dock can own the whiteboard and the store stays out of
     * the drawing business. Nothing clinical travels here — form answers go over
     * authenticated HTTP.
     */
    // Wrapped with a counter so two identical payloads in a row (two late
    // joiners both asking for the board) still register as separate events —
    // a bare value would compare equal and the watcher would miss the second.
    const appMessage = ref(null);
    let appMessageSeq = 0;
    function onDailyAppMessage(event) {
        appMessage.value = event?.data ? { seq: ++appMessageSeq, data: event.data } : null;
    }
    /**
     * @param {object} payload
     * @param {string} [to] a Daily session id to reach one person; defaults to
     *   everyone, which is what the whiteboard wants.
     */
    function sendAppMessage(payload, to = '*') {
        try {
            call?.sendAppMessage(payload, to);
        } catch {
            // a dropped stroke isn't worth surfacing
        }
    }

    function syncDailyParticipants() {
        if (!call) return;
        const all = call.participants() || {};
        const map = {};
        for (const [sid, p] of Object.entries(all)) {
            map[sid] = {
                sessionId: sid,
                userId: p.user_id,
                userName: p.user_name,
                local: !!p.local,
                audioOn: p.tracks?.audio?.state === 'playable',
                videoOn: p.tracks?.video?.state === 'playable',
                videoTrack: p.tracks?.video?.persistentTrack || null,
                audioTrack: p.tracks?.audio?.persistentTrack || null,
                // A screen share is a second track on the SAME participant, not a
                // separate one — it gets its own surface rather than a tile.
                screenOn: p.tracks?.screenVideo?.state === 'playable',
                screenTrack: p.tracks?.screenVideo?.persistentTrack || null,
            };
        }
        dailyParticipants.value = map;
    }

    async function teardownDaily() {
        if (!call) return;
        // Our own getDisplayMedia stream, so ours to stop. Without this the
        // browser's "You are sharing your screen" bar — and the capture behind
        // it — outlives the call.
        stopScreenShare();
        try {
            await call.leave();
        } catch {
            // ignore — we're tearing down
        }
        try {
            call.destroy();
        } catch {
            // ignore
        }
        call = null;
        joined.value = false;
        dailyParticipants.value = {};
    }

    async function close() {
        generation += 1; // anything still in flight belongs to a session that is over
        stopPolling();
        await teardownDaily();
        open.value = false;
        dockState.value = 'docked';
        addOpen.value = false;
        addStage.value = 'search';
        addedContact.value = null;
        meeting.value = null;
        inviteChannels.value = {};
        shareMenuOpen.value = false;
        encounterForms.value = [];
        // Leaving these set showed the PREVIOUS session's answer to "no forms yet"
        // vs "no encounter yet" while the next one loaded.
        hasEncounter.value = false;
        formsLoading.value = false;
        invitingId.value = null;
        addBusy.value = false;
        shareBusy.value = false;
        appMessage.value = null;
        screenSharing.value = false;
        screenShareError.value = '';
        currentContextId = null;
        sessionContextId.value = null;
        sessionContextType.value = null;
        stopElapsed();
    }

    // ------------------------------------------------------------------
    // Screen share (host only)
    // ------------------------------------------------------------------

    const screenSharing = ref(false);
    const screenShareError = ref('');

    /** Whoever is currently sharing a screen, or null. At most one is expected. */
    const screenShare = computed(() => {
        const sharer = Object.values(dailyParticipants.value).find((p) => p.screenOn && p.screenTrack);
        if (!sharer) return null;
        return {
            sessionId: sharer.sessionId,
            track: sharer.screenTrack,
            local: sharer.local,
            name: sharer.local ? 'You' : (rosterFor(sharer)?.displayName ?? sharer.userName ?? 'Participant'),
        };
    });

    /**
     * Capture options, and the reason for each.
     *
     * We call getDisplayMedia ourselves rather than letting Daily do it, because
     * the option that actually protects the chart — excluding whole-screen
     * capture — is not in Daily's typed options. Daily accepts a ready-made
     * stream, so nothing is lost.
     *
     * None of this can blacklist a domain: no browser API lets a page filter the
     * picker by origin. What it can do is remove the surfaces most likely to
     * expose the chart, and refuse a capture that got through anyway.
     */
    const DISPLAY_MEDIA = {
        audio: false,
        // Bias the picker toward a tab rather than a whole app window.
        video: { displaySurface: 'browser' },
        // Keep THIS Fresh tab out of the picker. Note it excludes one tab, not
        // the domain — another Fresh tab is still offerable and undetectable.
        selfBrowserSurface: 'exclude',
        // No whole-screen option. This is the one that matters: a monitor share
        // shows the chart no matter which window has focus.
        monitorTypeSurfaces: 'exclude',
        // Without this, the surface can be swapped mid-share, sailing past the
        // check below — a start-time guard alone would be defeatable by design.
        surfaceSwitching: 'exclude',
    };

    /** Surfaces we refuse even if the browser offered them anyway. */
    const BLOCKED_SURFACES = ['monitor'];

    /**
     * The stream WE created, kept so we can stop it ourselves.
     *
     * We hand Daily a ready-made stream rather than letting it call
     * getDisplayMedia, which makes the tracks ours to release. Relying on
     * `call.stopScreenShare()` alone risks leaving the capture — and the
     * browser's "You are sharing your screen" bar — running after the session.
     * Not reactive: nothing renders from it.
     */
    let screenStream = null;

    async function startScreenShare() {
        if (!call || screenSharing.value) return;
        screenShareError.value = '';
        let stream;
        try {
            stream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA);
        } catch (e) {
            // Cancelling the picker is a normal outcome, not an error to report.
            if (e?.name !== 'NotAllowedError' && e?.name !== 'AbortError') {
                screenShareError.value = 'Your browser could not start screen sharing.';
            }
            return;
        }

        // Backstop: a browser that ignored the options above still must not put
        // a whole screen — and therefore the chart — on the call.
        const track = stream.getVideoTracks()[0];
        const surface = track?.getSettings?.().displaySurface;
        if (!track || BLOCKED_SURFACES.includes(surface)) {
            stream.getTracks().forEach((t) => t.stop());
            screenShareError.value = 'Sharing a whole screen would show the chart. Pick a single window or tab instead.';
            return;
        }

        // The browser's own "Stop sharing" bar bypasses our button entirely.
        track.addEventListener('ended', () => stopScreenShare(), { once: true });

        try {
            call.startScreenShare({ mediaStream: stream });
            screenStream = stream;
        } catch {
            stream.getTracks().forEach((t) => t.stop());
            screenShareError.value = 'Screen sharing could not be started.';
        }
    }

    function stopScreenShare() {
        try {
            call?.stopScreenShare();
        } catch {
            // Already stopped — the state comes from Daily's event either way.
        }
        // Then release the capture itself. Telling Daily to stop sending it does
        // not necessarily stop a stream it did not open.
        if (screenStream) {
            screenStream.getTracks().forEach((t) => t.stop());
            screenStream = null;
        }
    }

    const toggleScreenShare = () => (screenSharing.value ? stopScreenShare() : startScreenShare());

    // ------------------------------------------------------------------
    // Media controls (local)
    // ------------------------------------------------------------------

    function toggleMic() {
        localAudio.value = !localAudio.value;
        call?.setLocalAudio(localAudio.value);
    }
    function toggleCamera() {
        localVideo.value = !localVideo.value;
        call?.setLocalVideo(localVideo.value);
    }

    // Host is a Daily owner, so these act on remote participants directly.
    function muteParticipant(sessionId) {
        if (sessionId) call?.updateParticipant(sessionId, { setAudio: false });
    }
    function removeParticipant(sessionId) {
        if (sessionId) call?.updateParticipant(sessionId, { eject: true });
    }

    // ------------------------------------------------------------------
    // Roster actions (Fresh endpoints)
    // ------------------------------------------------------------------

    // setInterval keeps firing while a slow poll is still outstanding. Without this
    // guard a backend answering in more than one interval gets an ever-growing
    // pile of identical requests — and the pile is then what keeps it slow.
    let refreshInFlight = false;
    async function refresh() {
        if (!meeting.value?.id || refreshInFlight) return;
        refreshInFlight = true;
        const gen = generation;
        try {
            const res = await getMeeting(meeting.value.id);
            if (res?.success && isCurrent(gen)) meeting.value = res.data;
        } catch {
            // keep last state on a transient poll error
        } finally {
            refreshInFlight = false;
        }
    }
    /** How often to poll right now — faster while a shared form can change. */
    const pollInterval = computed(() => (sharedContent.value?.kind === 'form' ? POLL_MS_SHARING_FORM : POLL_MS));

    function startPolling() {
        stopPolling();
        pollTimer = setInterval(refresh, pollInterval.value);
    }
    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    // The rate is fixed when the interval is created, so sharing a form after the
    // session started would otherwise keep the slow one. Declared below both
    // `pollInterval` and `startPolling` — a watcher placed above the computed it
    // reads throws on setup and Vue swallows it.
    watch(pollInterval, () => {
        if (pollTimer) startPolling();
    });

    async function loadInviteAvailability() {
        if (!meeting.value?.id) return;
        try {
            const res = await getInviteAvailability(meeting.value.id);
            if (res?.success) {
                inviteChannels.value = Object.fromEntries((res.data || []).map((p) => [p.id, { sms: p.sms, email: p.email }]));
            }
        } catch {
            // non-fatal
        }
    }

    async function admit(participantId) {
        try {
            const res = await admitParticipant(meeting.value.id, participantId);
            if (res?.success) meeting.value = res.data;
        } catch {
            // next poll reconciles
        }
    }
    // The backend admits everyone waiting in a single call when passed 'all'.
    const admitAll = () => admit('all');

    /**
     * Move someone back to the waiting room so the clinician can speak to the
     * rest privately. Reversed with admit().
     *
     * The audio has to stop the moment the button is pressed — a private word
     * that the person can still hear for five seconds is not a private word. So
     * this tells their browser to leave the call directly over the data channel
     * rather than waiting for their next poll to notice. The poll is what then
     * puts the waiting-room screen in front of them.
     *
     * @param {string} participantId our participant id
     * @param {?string} sessionId    their Daily session id, when they are connected
     */
    async function hold(participantId, sessionId) {
        // Straight down the data channel first: this is the part that has to be
        // immediate, and it does not depend on the request below succeeding.
        if (sessionId) sendAppMessage({ t: 'th-hold' }, sessionId);

        try {
            const res = await holdParticipant(meeting.value.id, participantId);
            if (res?.success) meeting.value = res.data;
        } catch {
            // next poll reconciles
        }
    }

    async function invite(participantId, channel) {
        if (invitingId.value) return;
        invitingId.value = participantId;
        try {
            const res = await inviteParticipant(meeting.value.id, participantId, channel);
            notify().add(
                res?.success ? { message: channel === 'sms' ? 'Text invite sent.' : 'Email invite sent.', color: 'success' } : { message: res?.error || 'Could not send the invite.', color: 'error' },
            );
        } catch (e) {
            notify().add({ message: e?.response?.data?.error || 'Could not send the invite.', color: 'error' });
        } finally {
            invitingId.value = null;
        }
    }

    // ------------------------------------------------------------------
    // Sharing (#5040)
    // ------------------------------------------------------------------

    /**
     * The forms attached to the encounter behind this appointment — what the
     * share menu offers. Sharing never creates a form: a clinician adds one
     * through the normal form flows, and this only surfaces what is there.
     * An ad-hoc session has no encounter and seeds its one form instead.
     *
     * Loaded on demand when the menu opens rather than polled, since it changes
     * only when someone adds a form.
     */
    async function loadEncounterForms() {
        if (!currentContextId || formsLoading.value) return;
        // An ad-hoc form session has no encounter to search: its one form was
        // seeded when the session opened, so leave it alone.
        if (sessionContextType.value === 'form') return;
        // Three sequential FHIR reads. The host can close the dock and start a
        // different appointment inside that window, and these results would then
        // offer a form from the wrong chart.
        const gen = generation;
        formsLoading.value = true;
        try {
            const encounters = await $httpFhirApi.get(`/Encounter?appointment=${currentContextId}`);
            if (!isCurrent(gen)) return;
            const encounterIds = (encounters?.data ?? []).map((e) => e.id).filter(Boolean);
            hasEncounter.value = encounterIds.length > 0;
            if (!encounterIds.length) {
                encounterForms.value = [];
                return;
            }

            // Drive the list off the form TASKS, not the responses: a form that
            // has been added but never opened has no QuestionnaireResponse yet
            // (task status 'accepted', no focus). Listing responses hid those
            // entirely, so an encounter with three forms showed one.
            const bundle = await $httpFhirApi.get(`/Task?encounter=${encounterIds.join(',')}&code=form&_include=Task:focus`);
            if (!isCurrent(gen)) return;
            const rows = bundle?.data ?? [];
            const tasks = rows.filter((r) => r.resourceType === 'Task');
            const responses = new Map(rows.filter((r) => r.resourceType === 'QuestionnaireResponse' && r.id).map((r) => [r.id, r]));

            // Titles live on the Questionnaire, which each task names by canonical
            // ("<url>|<version>"). Resolve them in one batch rather than per row.
            const canonicalOf = (task) => task.code?.coding?.[0]?.version ?? '';
            const urls = [...new Set(tasks.map((t) => canonicalOf(t).split('|')[0]).filter(Boolean))];
            let questionnaires = [];
            if (urls.length) {
                const qBundle = await $httpFhirApi.get(`/Questionnaire?url=${urls.join(',')}&_elements=url,version,title`);
                if (!isCurrent(gen)) return;
                questionnaires = qBundle?.data ?? [];
            }

            encounterForms.value = tasks.map((task) => {
                const [url, version] = canonicalOf(task).split('|');
                const definition = questionnaires.find((q) => q.url === url && (!version || String(q.version) === version)) || questionnaires.find((q) => q.url === url);
                const responseId = task.focus?.id ?? null;
                return {
                    id: responseId,
                    taskId: task.id,
                    // Who the form is assigned to, so the dock can open the one
                    // this clinician is expected to write rather than guessing.
                    ownerId: task.owner?.id ?? null,
                    // Fall back to the canonical's last segment so a form is never
                    // an anonymous "Form" just because its definition didn't load.
                    title: definition?.title || (url ? decodeURIComponent(url.split('/').pop()) : 'Form'),
                    status: responseId ? (responses.get(responseId)?.status ?? null) : null,
                    // Sharing points at a QuestionnaireResponse, so a form that has
                    // never been opened has nothing to point at yet.
                    ready: !!responseId,
                };
            });
        } catch {
            if (isCurrent(gen)) encounterForms.value = [];
        } finally {
            // Always clear, even for a stale generation. This is the flag that
            // gates re-entry at the top of this function, so leaving it set
            // because the session moved on would disable the share row for the
            // rest of the session with no way back.
            formsLoading.value = false;
        }
    }

    /**
     * The form the host should land on: the one assigned to them, else the first
     * open one. Deliberately does NOT require a QuestionnaireResponse to exist —
     * opening the filler is what creates it, so a form that has never been
     * opened is still a perfectly good place to land. (It could not be shared
     * directly for that reason, but sharing now happens from the filler.)
     */
    function formForHost(practitionerId) {
        const open = encounterForms.value.filter((f) => f.status !== 'completed');
        return open.find((f) => practitionerId && f.ownerId === practitionerId) ?? open[0] ?? null;
    }

    function openShareMenu() {
        shareMenuOpen.value = true;
        loadEncounterForms();
    }

    /**
     * Share a form or the whiteboard. Adding a form to the encounter is a
     * separate act — this is only the deliberate second click that puts it in
     * front of the participant, and it always lands read-only for them.
     */
    async function share(kind, contentRef, title) {
        if (!meeting.value || shareBusy.value) return false;
        const gen = generation;
        shareBusy.value = true;
        try {
            const res = await apiShareContent(meeting.value.id, kind, contentRef, title);
            if (!isCurrent(gen)) return false;
            if (res?.success) {
                meeting.value = res.data;
                shareMenuOpen.value = false;
                return true;
            }
            notify().add({ message: res?.error || 'Could not share that.', color: 'error' });
            return false;
        } catch (e) {
            notify().add({ message: e?.response?.data?.error || 'Could not share that.', color: 'error' });
            return false;
        } finally {
            if (isCurrent(gen)) shareBusy.value = false;
        }
    }

    const shareForm = (questionnaireResponseId, title) => share('form', questionnaireResponseId, title);
    const shareWhiteboard = () => share('whiteboard', null, 'Whiteboard');

    async function unshare() {
        if (!meeting.value) return;
        const gen = generation;
        try {
            const res = await apiUnshareContent(meeting.value.id);
            if (res?.success && isCurrent(gen)) meeting.value = res.data;
        } catch {
            // next poll reconciles
        }
    }

    /**
     * Flip who holds the pen. The recipient is named, not implied: a session can
     * hold a client and their family, and handing it to "the participant" would
     * otherwise let any of them write into the chart. Defaults to the client on
     * the call, falling back to whoever else is admitted.
     */
    const penCandidate = computed(() => {
        const admitted = roster.value.filter((p) => p.presence === 'admitted' || p.presence === 'in_session');
        return admitted.find((p) => p.type === 'client') ?? admitted[0] ?? null;
    });

    async function togglePen() {
        if (!meeting.value || !sharedContent.value) return;
        const next = participantCanEdit.value ? 'host' : 'participant';
        if (next === 'participant' && !penCandidate.value) {
            notify().add({ message: 'Admit someone before giving them edit access.', color: 'warning' });
            return;
        }
        const gen = generation;
        try {
            const res = await apiSetPen(meeting.value.id, next, next === 'participant' ? penCandidate.value.id : null);
            if (!isCurrent(gen)) return;
            if (res?.success) meeting.value = res.data;
            else notify().add({ message: res?.error || 'Could not change edit rights.', color: 'error' });
        } catch (e) {
            notify().add({ message: e?.response?.data?.error || 'Could not change edit rights.', color: 'error' });
        }
    }

    function openAdd() {
        addOpen.value = true;
        addStage.value = 'search';
        addedContact.value = null;
    }

    async function addContact(item) {
        if (!item?.id || !item?.resourceType || addBusy.value) return;
        addBusy.value = true;
        try {
            const res = await apiAddParticipant(meeting.value.id, { resourceType: item.resourceType, id: item.id });
            if (res?.success) {
                meeting.value = res.data;
                await loadInviteAvailability();
                const ch = inviteChannels.value[res.participantId] || { sms: false, email: false };
                addedContact.value = { id: res.participantId, displayName: item.display || 'Contact', sms: ch.sms, email: ch.email };
                addStage.value = 'invite';
            } else {
                notify().add({ message: res?.error || 'Could not add that contact.', color: 'error' });
            }
        } catch (e) {
            notify().add({ message: e?.response?.data?.error || 'Could not add that contact.', color: 'error' });
        } finally {
            addBusy.value = false;
        }
    }

    async function inviteAdded(channel) {
        if (channel) await invite(addedContact.value.id, channel);
        addOpen.value = false;
        addStage.value = 'search';
        addedContact.value = null;
    }

    async function endSession() {
        if (meeting.value?.id) {
            try {
                await endMeeting(meeting.value.id);
            } catch {
                // closing anyway
            }
        }
        await close();
    }

    // ------------------------------------------------------------------
    // Dock chrome
    // ------------------------------------------------------------------
    const setDockState = (s) => {
        dockState.value = s;
    };
    const collapse = () => setDockState('collapsed');
    const expand = () => setDockState(dockState.value === 'fullscreen' ? 'docked' : 'fullscreen');

    return {
        // state
        open,
        dockState,
        loading,
        errorMsg,
        meeting,
        inviteChannels,
        invitingId,
        addOpen,
        addStage,
        addedContact,
        addBusy,
        joined,
        localAudio,
        localVideo,
        dailyParticipants,
        // derived
        tiles,
        roster,
        // lifecycle
        openForAppointment,
        openAdhocForForm,
        close,
        endSession,
        // media
        toggleMic,
        toggleCamera,
        screenSharing,
        screenShareError,
        screenShare,
        toggleScreenShare,
        startScreenShare,
        stopScreenShare,
        muteParticipant,
        removeParticipant,
        // sharing
        sessionContextId,
        sessionContextType,
        elapsed,
        shareMenuOpen,
        shareBusy,
        sharedContent,
        isSharing,
        participantCanEdit,
        encounterForms,
        formsLoading,
        hasEncounter,
        loadEncounterForms,
        formForHost,
        openShareMenu,
        shareForm,
        shareWhiteboard,
        unshare,
        togglePen,
        appMessage,
        sendAppMessage,
        // roster
        admit,
        admitAll,
        hold,
        invite,
        openAdd,
        addContact,
        inviteAdded,
        loadInviteAvailability,
        // chrome
        setDockState,
        collapse,
        expand,
    };
});
